'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const STORAGE_KEY = 'oea_consent_v1';
const COOKIE_NAME = 'oea_consent';

interface ConsentState {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  ts: number;
}

function readConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ConsentState) : null;
  } catch {
    return null;
  }
}

function persist(consent: ConsentState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  const oneYear = 60 * 60 * 24 * 365;
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(
    JSON.stringify(consent),
  )}; path=/; max-age=${oneYear}; SameSite=Lax`;
}

export function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const c = readConsent();
    if (!c) setOpen(true);
  }, []);

  const acceptAll = () => {
    persist({ necessary: true, analytics: true, marketing: true, ts: Date.now() });
    setOpen(false);
  };

  const rejectOptional = () => {
    persist({
      necessary: true,
      analytics: false,
      marketing: false,
      ts: Date.now(),
    });
    setOpen(false);
  };

  const saveConfig = () => {
    persist({ necessary: true, analytics, marketing, ts: Date.now() });
    setConfigOpen(false);
    setOpen(false);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed inset-x-3 bottom-3 z-[55] sm:inset-x-6 sm:bottom-6"
          >
            <div className="glass mx-auto flex max-w-4xl flex-col gap-4 rounded-2xl px-5 py-4 shadow-premium sm:px-6 sm:py-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex gap-3">
                <Cookie
                  className="mt-1 h-5 w-5 flex-shrink-0 text-gold-400"
                  aria-hidden
                />
                <div className="space-y-1.5">
                  <h2 className="font-display text-sm font-semibold text-cream sm:text-base">
                    Tu privacidad importa
                  </h2>
                  <p className="text-xs leading-relaxed text-cream/70 sm:text-sm">
                    Usamos cookies técnicas (esenciales para que la plataforma
                    funcione) y, si lo aceptas, analíticas privacy-first para
                    mejorar el servicio. Puedes configurarlas o leer la{' '}
                    <Link
                      href="/legal/politica-cookies"
                      className="text-gold-300 underline-offset-4 hover:underline"
                    >
                      política completa
                    </Link>
                    .
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap lg:flex-nowrap">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfigOpen(true)}
                >
                  Configurar
                </Button>
                <Button size="sm" variant="outline" onClick={rejectOptional}>
                  Rechazar opcionales
                </Button>
                <Button size="sm" variant="primary" onClick={acceptAll}>
                  Aceptar todas
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Preferencias de cookies</DialogTitle>
            <DialogDescription>
              Activa o desactiva categorías de cookies. Las técnicas son
              imprescindibles para que la plataforma funcione correctamente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 rounded-xl border border-white/10 bg-navy-950/40 p-4">
              <div>
                <h3 className="font-medium text-cream">Cookies técnicas</h3>
                <p className="mt-1 text-sm text-cream/65">
                  Necesarias para autenticación, seguridad y guardado del
                  progreso del curso. No se pueden desactivar.
                </p>
              </div>
              <Switch checked disabled />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-xl border border-white/10 bg-navy-950/40 p-4">
              <div>
                <h3 className="font-medium text-cream">Analíticas</h3>
                <p className="mt-1 text-sm text-cream/65">
                  Usamos Plausible (privacy-first, sin cookies de tracking) para
                  entender qué contenido es útil. Datos agregados, sin
                  identificación personal.
                </p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-xl border border-white/10 bg-navy-950/40 p-4">
              <div>
                <h3 className="font-medium text-cream">Marketing</h3>
                <p className="mt-1 text-sm text-cream/65">
                  Activas si quieres recibir comunicaciones personalizadas y que
                  optimicemos campañas. Cero spam, máximo 1-2 emails al mes.
                </p>
              </div>
              <Switch checked={marketing} onCheckedChange={setMarketing} />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={() => setConfigOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={saveConfig}>
              Guardar preferencias
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
